import { AccountStatus } from '@/swagger/constants/anagraphics-enums';

export const en = {
  department: {
    pageTitle: 'My Department',
    team: {
      pageTitle: 'My Team',
    },
    tabs: {
      overview: 'Department Overview',
      overviewTeam: 'Team Overview',
      assessment: 'Assessment',
    },
    dataMismatchWarning: 'If there is any data inconsistency, please contact the administrator',
    details: {
      description: 'Select a questionnaire from the list below to view the results.',
      manager: 'Manager: ',
      departmentManager: 'Department Manager',
      teamManager: 'Team Manager',
      noDepartmentName: 'No name',
      noTeamName: 'Department members',
      validProfiles: 'Valid profiles',
      profilesToCorrect: 'Profiles to correct',
      team: 'Team',
      teamMembers: 'Team members',
      members: 'Members',
      tabs: {
        profile: 'Profile',
        assessment: 'Assessment',
      },
      status: {
        [AccountStatus.ENABLED]: 'Active',
        [AccountStatus.PENDING]: 'Pending',
        [AccountStatus.DISABLED]: 'Disabled',
        [AccountStatus.NOT_INVITED]: 'Not active',
      },
      tableEmployees: {
        title: 'Employee list',
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        role: 'Company Role',
        department: 'Department',
        status: {
          label: 'User status',
          tooltip:
            'Indicates the user status on the Slash platform. It can be Active, Pending, Disabled, or Not active.',
        },
      },
      assessment: {
        title: '{{name}} assessment',
        description: 'Select a questionnaire from the list below to view the results.',
        status: {
          completed: 'Completed on {{date}}',
          notCompleted: 'Sent',
          noSurvey: 'Not sent',
        },
        noResultsVisible: {
          title: 'Results for {{templateName}} are not available',
          description:
            'Assessment results are currently not visible. For more information or details, contact HR.',
        },
        noData: {
          title: 'Assessment data not available',
          descriptionEmployee:
            'No assessment data is available for this employee. For more information, contact the platform Admin.',
          descriptionTeam:
            'No assessment has been sent to any member of your team. For more information, contact the platform Admin.',
          descriptionDepartment:
            'No assessment has been sent to any member of your department. For more information, contact the platform Admin.',
        },
        intro: {
          description: {
            single:
              'The data refers to users who have completed the assessment on “{{nameSurvey}}”. The analysis base consists of {{totalCompletedSurveys}} user.',
            plural:
              'The data refers to users who have completed the assessment on “{{nameSurvey}}”. The analysis base consists of {{totalCompletedSurveys}} users.',
          },
        },
        descriptionNotSent:
          'The assessment has not yet been sent to this user. For more information, contact the platform admin.',
        descriptionCompleted:
          'The chart shows the average scores (scale 0-7) on the {{metricsCount}} {{surveyTemplateName}}, considered essential to effectively face AI challenges.',
        descriptionNotCompleted:
          'The assessment has been sent but the user has not completed it yet.',
        generalDescriptionDepartment:
          'The data refers to users in the {{departmentName}} department who have completed the soft skills assessment. The analysis base consists of {{invitedUser}} users.',
        generalDescriptionTeam:
          'The data refers to users in the {{teamName}} team who have completed the soft skills assessment. The analysis base consists of {{invitedUser}} users.',
        descriptionTitle: 'Description',
        surveyResults: {
          title: 'Score for {{surveyTemplateName}}',
          description:
            'The chart shows the average scores (scale 0-7) on the 6 soft skills, considered essential to effectively face AI challenges.',
          insightBox: {
            title: 'Insights',
            proTitle: 'Strengths',
            conTitle: 'Weaknesses',
          },
          alertBadge:
            'The model is undergoing scientific validation and shows a consistent level of reliability, especially in aggregated views for teams and departments',
        },
        genericSurveyResults: {
          title: 'Soft Skills Assessment',
          titleAverage: 'Average score',
          genericAverageGraphTitle: 'Average score for {{surveyTemplateName}}',
          description:
            'The chart shows the average scores (scale 0-7) on the {{metricsCount}} {{surveyTemplateName}}, considered essential to effectively face AI challenges.',
          overview: {
            completed: 'Completed questionnaires',
            invited: 'Sent questionnaires',
            toComplete: 'Questionnaires to complete',
            completionRate: 'Completion rate',
          },
          insightBox: {
            title: 'Insights',
            proTitle: 'Strengths',
            conTitle: 'Weaknesses',
          },
        },
        resultsVsOrganization: {
          title: 'Performance compared to Team {{teamName}} and Department {{departmentName}}',
          description:
            'Comparison between the employee’s average scores, the team, and the department.',
          labelTooltipChart: 'Score: {{current}} pts\n\n{{teamValue}} vs team average',
          legend: {
            yourScore: 'Personal',
            teamAverage: 'Team {{teamName}}',
          },
        },
        resultsDepartmentVsOrganization: {
          title: 'Department performance compared to organization',
          description:
            'Comparison between the department’s average scores and those of the entire organization.',
          labelTooltipChart: 'Score: {{current}} pts\n\n{{diffValue}} vs organization average',
          legend: {
            depAverage: 'Department ({{total}} people)',
            orgAverage: 'Organization ({{total}} people)',
          },
        },
        resultsTeamVsDepartmentVsOrganization: {
          title: 'Team performance compared to department and organization',
          description:
            'Comparison between the team’s average scores, the department, and the organization.',
          labelTooltipChart:
            'Score: {{current}} pts\n\n{{depValue}} vs department average\n\n{{orgValue}} vs organization average',
          legend: {
            teamAverage: 'Team ({{total}} people)',
            depAverage: 'Department ({{total}} people)',
            orgAverage: 'Organization ({{total}} people)',
          },
        },
        resultsTeamVsDepartment: {
          title: 'Team performance compared to department',
          description: 'Comparison between the team’s average scores and the department.',
          labelTooltipChart: 'Score: {{current}} pts\n\n{{depValue}} vs department average',
          legend: {
            teamAverage: 'Team ({{total}} people)',
            depAverage: 'Department ({{total}} people)',
          },
        },
        resultsEmployeeVsTeamVsDepartment: {
          title: 'Employee performance compared to team and department',
          description:
            'Comparison between the employee’s average scores, the team, and the department.',
          labelTooltipChart:
            'Score: {{current}} pts\n\n{{teamValue}} vs team average\n\n{{depValue}} vs department average',
          legend: {
            employee: 'Employee',
            teamAverage: 'Team ({{total}} people)',
            depAverage: 'Department ({{total}} people)',
          },
        },
        resultsEmployeeVsTeam: {
          title: 'Employee performance compared to team',
          description: 'Comparison between the employee’s average scores and the team.',
          labelTooltipChart: 'Score: {{current}} pts\n\n{{depValue}} vs department average',
          legend: {
            employee: 'Employee',
            teamAverage: 'Team ({{total}} people)',
          },
        },
        distributionBox: {
          title: 'Score distribution by skill',
          description:
            'The distribution shows the percentage of department users placed in the three skill levels for each skill.',
          descriptionTeam:
            'The distribution shows the percentage of team users placed in the three skill levels for each skill.',
        },
        surveyTitle: {
          notCompleted: '{{surveyTemplateName}} score',
          completed: 'Average score for {{surveyTemplateName}}',
        },
      },
    },
    tutorial: {
      department: {
        title: 'Your Department',
        slides: {
          slide1: {
            title: 'Explore the department overview',
            description:
              'In the Department Overview tab, you can view the complete structure of your department: managers, teams, and activity status of individual members. From here, you can easily access team tabs or individual profiles.',
          },
          slide2: {
            title: 'Check your collaborators’ profiles',
            description:
              'From the Department Overview, you can access individual employee profiles. Each tab shows user information, individual scores, insights, and assessment completion dates.',
          },
          slide3: {
            title: 'View assessment results',
            description:
              'In the Assessment section, you can monitor the results of your department and individual teams. Compare results between teams and see how your department ranks against the company average.',
          },
          slide4: {
            title: 'Analyze team performance',
            description:
              'For each team, you can access a dedicated detail view that collects average results from different assessments, highlighting strengths and areas for improvement. Radar charts and level distributions help you quickly interpret team performance and identify where to take targeted development actions.',
          },
        },
      },
      team: {
        title: 'Your Team',
        slides: {
          slide1: {
            title: 'Explore your team overview',
            description:
              'In the Team Overview tab, you can check the group structure, verify roles, user status, and active members. From here, you can access individual profiles and view member details.',
          },
          slide2: {
            title: 'Check member profiles',
            description:
              'Access individual profiles to review assessment results, with a clear overview of each person’s strengths and areas for improvement.',
          },
          slide3: {
            title: 'View team assessment results',
            description:
              'In the Assessment section, you can monitor your team’s results and compare them with the department average. Radar and bar charts show the distribution of skill levels for each soft skill.',
          },
        },
      },
    },
  },
};
